import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Check, Inbox, Loader2, Pill, RefreshCw, Search, Send, X, Stethoscope } from 'lucide-react';
import { DOSE_APPROVAL_VALID_DAYS } from '@bloom/shared';
import { apiClient } from '../lib/api';
import { careInboxUnreadKey } from '../hooks/useCareInboxUnreadCount';

/**
 * The clinic's care inbox: patients' messages and refill requests from the
 * patient portal. Polls rather than streams; patients wait minutes for a
 * reply, not seconds.
 */

interface ConversationSummary {
  _id: string;
  patientId: string;
  patientName: string;
  lastMessageAt: string;
  lastMessageAuthor?: 'patient' | 'clinic';
  lastMessagePreview: string;
  unreadCount: number;
  openRefillRequests: number;
  /** Set when a telemed provider answers this thread in 5 Axis Core. */
  handledBy?: HandledBy;
}

interface HandledBy { destination: 'telemed'; networkName?: string; providerName?: string }

interface InboxMessage {
  _id: string;
  seq: number;
  author: 'patient' | 'clinic';
  authorName: string;
  kind: 'message' | 'refill_request';
  body: string;
  createdAt: string;
  refill?: { name: string; requestType?: 'refill' | 'dose_change'; doseChange?: { programName: string; fromDoseLabel?: string; toDoseLabel: string; steps: number }; orderNumber?: string; status: 'open' | 'approved' | 'declined'; decidedAt?: string; decisionNote?: string };
  route?: { destination: 'clinic_inbox' | 'core'; networkName?: string; providerName?: string };
  delivery?: { status: 'pending' | 'sent' | 'failed' | 'dead'; attempts: number; lastError?: string; sentAt?: string };
  fromCore?: boolean;
}

interface ThreadResponse {
  conversation: {
    _id: string;
    patient: { _id: string; name: string; phone?: string; email?: string; status: string } | null;
    latestSeq: number;
    clinicReadSeq: number;
    patientReadSeq: number;
    handledBy?: HandledBy;
  };
  messages: InboxMessage[];
  hasMore: boolean;
  canReply: boolean;
}

type Filter = 'all' | 'unread' | 'refills' | 'telemed';
const POLL_MS = 30_000;
const PAGE_SIZE = 30;

interface ConversationPage {
  data: ConversationSummary[];
  pagination?: { total: number; skip: number; limit: number; hasMore: boolean };
}
const MAX_LENGTH = 2000;

function errorText(err: any, fallback: string) {
  return err?.response?.data?.error || fallback;
}

export function CareInbox() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Pages of conversations (the route serves 30 at a time); "Load more" walks
  // skip forward so older threads stay reachable. Search and filter run on the
  // server, and a new key starts again from the first page.
  const list = useInfiniteQuery({
    queryKey: ['care-inbox', filter, q],
    refetchInterval: POLL_MS,
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }) => {
      const query = new URLSearchParams({ filter, skip: String(pageParam), limit: String(PAGE_SIZE), ...(q && { q }) });
      return apiClient.get<ConversationPage>(`/care-inbox/conversations?${query}`, { signal });
    },
    getNextPageParam: (last, pages) => {
      if (!last.pagination?.hasMore) return undefined;
      return pages.reduce((n, p) => n + p.data.length, 0);
    },
  });
  const conversations = useMemo(() => {
    // A thread that moved up between page fetches can appear twice; keep the first.
    const seen = new Set<string>();
    return (list.data?.pages ?? []).flatMap(p => p.data).filter(c => (seen.has(c._id) ? false : (seen.add(c._id), true)));
  }, [list.data]);
  const totalConversations = list.data?.pages[list.data.pages.length - 1]?.pagination?.total ?? null;

  const select = (id: string | null) => setParams(id ? { c: id } : {}, { replace: false });

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Care inbox</h1>
          <p className="text-gray-600 mt-1">Messages and refill requests from your patients’ portal.</p>
        </div>
        <button type="button" onClick={() => list.refetch()} aria-label="Refresh"
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
          <RefreshCw className={`w-4 h-4 ${list.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid md:grid-cols-[340px_1fr] gap-4 min-h-[60vh]">
        <section aria-label="Conversations" className={`bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden ${selectedId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-gray-200 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patients" aria-label="Search patients"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-1.5" role="group" aria-label="Filter conversations">
              {(['all', 'unread', 'refills', 'telemed'] as Filter[]).map(value => (
                <button key={value} type="button" onClick={() => setFilter(value)} aria-pressed={filter === value}
                  className={`px-3 py-1 rounded-lg text-xs font-medium ${filter === value ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {value === 'all' ? 'All' : value === 'unread' ? 'Unread' : value === 'refills' ? 'Refill requests' : 'Telemed'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {list.isPending ? (
              <div className="p-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : list.isError ? (
              <div role="alert" className="p-4 text-sm text-red-700">{errorText(list.error, 'Could not load conversations.')}</div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">{filter === 'all' && !q ? 'No messages from patients yet.' : 'Nothing matches.'}</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {conversations.map((c: ConversationSummary) => (
                  <li key={c._id}>
                    <button type="button" onClick={() => select(c._id)} aria-current={c._id === selectedId ? 'true' : undefined}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${c._id === selectedId ? 'bg-blue-50' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${c.unreadCount ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{c.patientName}</span>
                        <span className="shrink-0 text-xs text-gray-500">{new Date(c.lastMessageAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="flex-1 truncate text-xs text-gray-500">{c.lastMessageAuthor === 'clinic' ? 'You: ' : ''}{c.lastMessagePreview}</span>
                        {c.handledBy && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 px-1.5 py-0.5 text-[10px] font-medium text-violet-800" title={`Answered by ${c.handledBy.networkName ?? 'the telemed network'} in 5 Axis Core`}><Stethoscope className="w-3 h-3" />Telemed</span>}
                        {c.openRefillRequests > 0 && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"><Pill className="w-3 h-3" />Refill</span>}
                        {c.unreadCount > 0 && <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white" aria-label={`${c.unreadCount} unread`}>{c.unreadCount}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {list.hasNextPage && (
              <div className="p-3 border-t border-gray-100 flex items-center justify-between gap-2 text-xs text-gray-500">
                <span>Showing {conversations.length}{totalConversations !== null ? ` of ${totalConversations}` : ''}</span>
                <button type="button" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </section>

        <section aria-label="Conversation" className={`bg-white rounded-lg border border-gray-200 flex-col overflow-hidden ${selectedId ? 'flex' : 'hidden md:flex'}`}>
          {selectedId ? (
            <Thread key={selectedId} conversationId={selectedId} onBack={() => select(null)} />
          ) : (
            <div className="m-auto p-8 text-center text-gray-500">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Select a conversation to read and reply.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Thread({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const key = ['care-inbox-thread', conversationId];
  const thread = useQuery<ThreadResponse>({
    queryKey: key,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: ThreadResponse }>(`/care-inbox/conversations/${conversationId}`);
      return res.data;
    },
  });
  const [older, setOlder] = useState<InboxMessage[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  const messages = useMemo(() => {
    const byId = new Map<string, InboxMessage>();
    for (const m of [...older, ...(thread.data?.messages ?? [])]) byId.set(m._id, m);
    return [...byId.values()].sort((a, b) => a.seq - b.seq);
  }, [older, thread.data]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['care-inbox'] });
    queryClient.invalidateQueries({ queryKey: careInboxUnreadKey });
  };

  // Mark read up to what's on screen.
  const newestSeq = messages.length ? messages[messages.length - 1].seq : 0;
  const lastMarked = useRef(0);
  useEffect(() => {
    if (!thread.data || newestSeq <= thread.data.conversation.clinicReadSeq || newestSeq <= lastMarked.current) return;
    lastMarked.current = newestSeq;
    apiClient.post(`/care-inbox/conversations/${conversationId}/read`, { upToSeq: newestSeq })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['care-inbox'] });
        queryClient.invalidateQueries({ queryKey: careInboxUnreadKey });
      })
      .catch(() => { lastMarked.current = 0; });
  }, [thread.data, newestSeq, conversationId, queryClient]);

  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [messages.length]);

  async function loadOlder() {
    if (!messages.length) return;
    const res = await apiClient.get<{ success: boolean; data: ThreadResponse }>(`/care-inbox/conversations/${conversationId}?before=${messages[0].seq}`);
    setOlder(prev => [...res.data.messages, ...prev]);
    setOlderHasMore(res.data.hasMore);
  }

  if (thread.isPending) return <div className="m-auto p-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (thread.isError) return <div role="alert" className="p-4 text-sm text-red-700">{errorText(thread.error, 'Could not load this conversation.')}</div>;

  const { conversation, canReply } = thread.data;
  const patient = conversation.patient;
  const hasMore = olderHasMore ?? thread.data.hasMore;
  return (
    <>
      <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <button type="button" onClick={onBack} className="md:hidden p-1 -ml-1 text-gray-500" aria-label="Back to conversations"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-900 truncate">{patient?.name ?? 'Patient'}</h2>
          {patient && <p className="text-xs text-gray-500 truncate">{[patient.phone, patient.email].filter(Boolean).join(' · ')}{patient.status !== 'active' ? ' · inactive' : ''}</p>}
        </div>
        {patient && <Link to={`/patients/${patient._id}`} className="ml-auto text-sm text-blue-600 hover:underline shrink-0">Patient profile</Link>}
      </header>

      {conversation.handledBy && (
        <div className="flex items-start gap-2 border-b border-violet-200 bg-violet-50 px-4 py-2.5 text-sm text-violet-900">
          <Stethoscope className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>This patient's prescriber{conversation.handledBy.providerName ? `, ${conversation.handledBy.providerName},` : ''} is with {conversation.handledBy.networkName ?? 'a telemed network'}. Their messages go to the provider in 5 Axis Core, and the provider's replies appear here. You can read along; replies and refill decisions come from the provider.</p>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 max-h-[60vh] md:max-h-none">
        {hasMore && (
          <div className="text-center"><button type="button" onClick={loadOlder} className="text-sm text-blue-600 hover:underline">Load earlier messages</button></div>
        )}
        {messages.map(m => (
          <div key={m._id} className={`flex flex-col ${m.author === 'clinic' ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm ${m.author === 'clinic' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
              {m.body}
            </div>
            <span className="mt-1 text-[11px] text-gray-500">
              {m.authorName} · {new Date(m.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              {m.author === 'clinic' && m.seq <= conversation.patientReadSeq && ' · Seen'}
              {m.fromCore && ' · via Core'}
              {m.delivery && <DeliveryNote delivery={m.delivery} />}
            </span>
            {m.kind === 'refill_request' && m.refill && <RefillCard message={m} canAct={canReply} onDecided={refreshAll} />}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {canReply ? (
        <ReplyBox conversationId={conversationId} onSent={refreshAll} />
      ) : (
        <p className="border-t border-gray-200 px-4 py-3 text-sm text-gray-500">{conversation.handledBy ? 'The telemed provider replies to this patient in 5 Axis Core.' : 'This account can read messages but not reply.'}</p>
      )}
    </>
  );
}

/** Where a patient's message to a telemed provider stands on its way to Core. */
function DeliveryNote({ delivery }: { delivery: NonNullable<InboxMessage['delivery']> }) {
  if (delivery.status === 'sent') return <> · Sent to Core</>;
  if (delivery.status === 'dead') return <span className="text-red-600" title={delivery.lastError}> · Not delivered to Core</span>;
  return <span className="text-amber-700" title={delivery.lastError}> · {delivery.status === 'failed' ? `Retrying delivery (attempt ${delivery.attempts})` : 'Sending to Core'}</span>;
}

const REFILL_LABEL = { open: 'Awaiting decision', approved: 'Approved', declined: 'Declined' } as const;

function RefillCard({ message, canAct, onDecided }: { message: InboxMessage; canAct: boolean; onDecided: () => void }) {
  const refill = message.refill!;
  const dose = refill.requestType === 'dose_change' ? refill.doseChange : undefined;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approve' | 'decline' | null>(null);
  const [error, setError] = useState('');

  async function decide(decision: 'approve' | 'decline') {
    setBusy(decision); setError('');
    try {
      await apiClient.post(`/care-inbox/refill-requests/${message._id}/decision`, { decision, note: note.trim() || undefined });
      onDecided();
    } catch (err) {
      setError(errorText(err, 'Could not record the decision.'));
      onDecided();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-2 w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-medium text-amber-900"><Pill className="w-4 h-4" />{dose ? `Dose change: ${dose.programName}` : `Refill: ${refill.name}`}</span>
        <span className="text-xs text-amber-800">{REFILL_LABEL[refill.status]}</span>
      </div>
      {dose && (
        <p className="mt-1 text-sm text-amber-900">
          <span className="font-medium">{dose.fromDoseLabel || 'Current dose'}</span> → <span className="font-medium">{dose.toDoseLabel}</span>
          <span className="text-xs text-amber-800"> · {dose.steps} dose steps</span>
        </p>
      )}
      {refill.orderNumber && <p className="mt-1 text-xs text-amber-800">Last order #{refill.orderNumber}</p>}
      {refill.status === 'open' && canAct && (
        <div className="mt-3 space-y-2">
          <label className="sr-only" htmlFor={`note-${message._id}`}>Note to the patient (optional)</label>
          <input id={`note-${message._id}`} value={note} onChange={e => setNote(e.target.value)} maxLength={500}
            placeholder="Note to the patient (optional)"
            className="w-full px-3 py-1.5 text-sm border border-amber-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
          {error && <p role="alert" className="flex items-center gap-1 text-xs text-red-700"><AlertCircle className="w-3 h-3" />{error}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={() => decide('approve')} disabled={!!busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60">
              {busy === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Approve
            </button>
            <button type="button" onClick={() => decide('decline')} disabled={!!busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
              {busy === 'decline' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}Decline
            </button>
          </div>
          <p className="text-[11px] text-amber-800">{dose
            ? `Patients change one dose step on their own; this is a bigger move. Approving lets the patient order ${dose.toDoseLabel} for ${DOSE_APPROVAL_VALID_DAYS} days.`
            : 'Approving tells the patient in this thread; place the refill order as usual.'}</p>
        </div>
      )}
    </div>
  );
}

function ReplyBox({ conversationId, onSent }: { conversationId: string; onSent: () => void }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const body = draft.trim();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!body || body.length > MAX_LENGTH) return;
    setSending(true); setError('');
    try {
      await apiClient.post(`/care-inbox/conversations/${conversationId}/messages`, { body });
      setDraft('');
      onSent();
    } catch (err) {
      setError(errorText(err, 'Not sent. Please try again.'));
    } finally {
      setSending(false);
    }
  }

  return (
    <form onSubmit={submit} className="border-t border-gray-200 p-3">
      {error && <p role="alert" className="mb-2 text-sm text-red-700">{error}</p>}
      <div className="flex items-end gap-2">
        <label htmlFor="care-reply" className="sr-only">Reply</label>
        <textarea id="care-reply" value={draft} onChange={e => setDraft(e.target.value)} rows={2} maxLength={MAX_LENGTH}
          placeholder="Write a reply. The patient is notified by text or email, without the message itself."
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e as unknown as FormEvent); }}
          className="flex-1 resize-y px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button type="submit" disabled={!body || sending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send
        </button>
      </div>
    </form>
  );
}

export default CareInbox;
